/**
 * The pure part of the team data layer.
 *
 * WHY THIS FILE EXISTS AT ALL. `api.ts` imports `expo/fetch`, and `expo/fetch`
 * pulls in react-native, which bun cannot parse — the same reason
 * `sync-failure.ts` exists beside `sync-engine.ts`. So every hook under
 * `src/hooks/use-*.ts` that calls the team API is unloadable under `bun test`.
 * The decisions worth proving therefore live here, where a test can reach them,
 * and the hooks stay thin enough to read in one sitting.
 *
 * Four decisions:
 *
 *   1. Which refusals a person standing at the button can act on, and how to
 *      read one off a thrown value without importing `ApiRequestError`.
 *   2. What a mutator answers, so no team mutator ever throws into a render.
 *   3. Which capabilities a level may hold, read from the server's catalogue
 *      and never from a copy of it here.
 *   4. What a job impact preview MEANS in people, which is not what it counts.
 */
import type {
  Authority,
  CapabilityCatalogueResponse,
  CapabilityRow,
  JobImpactResponse,
} from '@alluzo/shared';

/* ------------------------------------------------ what a screen shows next to
 *                                                            a button        */

/** The server's own code, and the server's own sentence for it. */
export type TeamRefusal = { readonly code: string; readonly message: string };

/**
 * The refusals a person can do something about.
 *
 * Every one of these arrives with a sentence the server wrote for a person to
 * read — "Make somebody else the owner first", "This business already has a job
 * with that name" — so a screen shows it beside the button that provoked it and
 * the list behind that button stays on screen. That is the whole point: a 409
 * on Save is not a reason to unmount a member list.
 *
 * Deliberately absent: BAD_BUSINESS_ID, BAD_USER_ID, BAD_JOB_ID, BAD_RECORD_ID,
 * BAD_INVITE_ID, BAD_TENANT_HEADER, TENANT_REQUIRED, TENANT_HEADER_NOT_ALLOWED,
 * INVALID_CLIENT_KEY, NO_SESSION. Each of those means THIS CODE sent the wrong
 * request, and there is nothing a person can type to fix it. Those go through
 * `describeApiFailure`, which says what is actually wrong.
 *
 * Source: the `refuse(...)` calls in apps/server/src/routes/{businesses,
 * members,jobs,grants}.ts. A code that appears there and not here is not
 * swallowed — it becomes a `failed`, with the server's sentence and the status.
 */
export const ACTIONABLE_REFUSALS: ReadonlySet<string> = new Set([
  // The business.
  'BUSINESS_NAME_INVALID',
  'BUSINESS_NOT_FOUND',
  'NOT_A_MEMBER',
  // The people.
  'MISSING_CAPABILITY',
  'LEVEL_ABOVE_YOURS',
  'CANNOT_CHANGE_PEER',
  'LAST_OWNER',
  'MEMBER_NOT_FOUND',
  'INVITE_INVALID',
  'INVITE_ROLE_OUTRANKS',
  'UNIT_UNKNOWN',
  // The job. BAD_REQUEST is here and only here on purpose: on a job write it
  // is the empty-name and the unknown-level sentence, both of which are about
  // what somebody filled in.
  'BAD_REQUEST',
  'JOB_NAME_TAKEN',
  'JOB_IS_OURS',
  'JOB_NOT_FOUND',
  'INVALID_SPEC',
  // The grant.
  'GRANT_NOT_FOUND',
  'REFOLLOW_NO_ROLE',
  'INSUFFICIENT_LEVEL',
  // Roles, people and access (web-redesign-02 phase 4). Each is a sentence
  // about what was asked for, said beside the control that asked.
  'ROLE_NOT_FOUND',
  'ROLE_NAME_TAKEN',
  'ROLE_NEEDED',
  'SYSTEM_ROLE_KEPT',
  'PERMISSION_NEEDS_ANOTHER',
  'MOVE_TO_NEEDED',
  'MOVE_TO_SAME',
  'ABOVE_YOUR_ROLE',
  'ABOVE_ROLE',
  'ABOVE_RECORD',
  'OWN_ROLE',
  'PRIMARY_ONLY',
  'PRIMARY_FIXED',
  'LAST_PRIMARY',
  'ALREADY_PRIMARY',
  'NOT_YOUR_RIGHT',
  'OUTSIDE_YOUR_TEAM',
  'REPORTS_TO_LOOP',
  'ADMIN_OPENS_ALL',
  'ADMIN_KEEPS_ACCESS',
  'RECORD_NOT_FOUND',
  'FIELD_UNKNOWN',
  'LIST_UNKNOWN',
]);

/** The three fields a refused `/v1` call carries, read without a class. */
type ThrownShape = { status: number | null; code: string | null; message: string | null };

/**
 * Structural, not `instanceof`.
 *
 * `ApiRequestError` is declared in `api.ts`, which this module must not import
 * or it stops being loadable under bun. Reading `status`, `code` and `message`
 * off the value costs nothing and works on the real error as well as on a plain
 * object a test hands over.
 */
const shapeOf = (thrown: unknown): ThrownShape => {
  if (typeof thrown !== 'object' || thrown === null) {
    return { status: null, code: null, message: null };
  }
  const held = thrown as { status?: unknown; code?: unknown; message?: unknown };
  return {
    status: typeof held.status === 'number' && Number.isFinite(held.status) ? held.status : null,
    code: typeof held.code === 'string' && held.code !== '' ? held.code : null,
    message:
      typeof held.message === 'string' && held.message.trim() !== '' ? held.message.trim() : null,
  };
};

/**
 * The refusal to show beside the button, or null when this is not one.
 *
 * Null covers three things, and they all belong to `describeApiFailure`: a
 * request that never reached a server, a 5xx, and a code this app does not
 * recognise. A 401 is null on purpose even though it carries a code — losing
 * the session is not something to answer next to a Save button.
 */
export const asTeamRefusal = (thrown: unknown): TeamRefusal | null => {
  const { status, code, message } = shapeOf(thrown);
  if (status === null || code === null || message === null) return null;
  if (status === 401 || status >= 500) return null;
  if (!ACTIONABLE_REFUSALS.has(code)) return null;
  return { code, message };
};

/* --------------------------------------------------- what a mutator answers */

/**
 * A team mutator NEVER throws.
 *
 * A rejected promise out of an `onPress` is an unhandled rejection, and the one
 * screen that forgets to catch it takes the list down with it. So both endings
 * are values: `refused` is a sentence for beside the button, with the server's
 * code so a screen can highlight the field that caused it, and `failed` is the
 * sentence `describeApiFailure` writes for everything else.
 */
export type TeamOutcome<T> =
  | { readonly kind: 'done'; readonly value: T }
  | { readonly kind: 'refused'; readonly code: string; readonly message: string }
  | { readonly kind: 'failed'; readonly message: string };

/** The sentence to show, whichever of the two endings it was. Null when done. */
export const outcomeMessage = <T,>(outcome: TeamOutcome<T>): string | null =>
  outcome.kind === 'done' ? null : outcome.message;

/* -------------------------------------------------------- once per app run */

/**
 * Run `load` at most once, and forget a failure.
 *
 * The same shape `apps/server/src/routes/capabilities.ts` uses for the same
 * answer, and for the same two reasons: the promise rather than the value, so
 * two screens mounting together make one round trip; and the slot cleared on
 * rejection, so a server that was not up yet is retried instead of being
 * remembered as a permanent error.
 */
export const oncePerRun = <T,>(load: () => Promise<T>): (() => Promise<T>) => {
  let held: Promise<T> | null = null;
  return (): Promise<T> => {
    held ??= load().catch((error: unknown) => {
      held = null;
      throw error;
    });
    return held;
  };
};

/* ------------------------------------------------------------ the catalogue */

/**
 * One row of the role editor: the capability, and whether this level may hold
 * it at all.
 *
 * `needsAuthority` is the LOWEST level that clears the row's `minRank` — the
 * word `cap.needs_level` puts in its sentence. Null when the level already
 * clears it, so a screen has one thing to test rather than two.
 */
export type CapabilityChoice = {
  readonly row: CapabilityRow;
  readonly allowed: boolean;
  readonly needsAuthority: Authority | null;
};

const rankOf = (catalogue: CapabilityCatalogueResponse, authority: Authority): number =>
  catalogue.authorities.find((one) => one.key === authority)?.rank ?? 0;

/**
 * The lowest level in the catalogue that clears `minRank`, or null when none
 * does. Read from the answer and never from `AUTHORITIES`: the ranks are seeded
 * by migration, and a copy of them here would be a second list to drift.
 */
export const lowestAuthorityFor = (
  catalogue: CapabilityCatalogueResponse,
  minRank: number,
): Authority | null => {
  let lowest: { key: Authority; rank: number } | null = null;
  for (const one of catalogue.authorities) {
    if (one.rank < minRank) continue;
    if (lowest === null || one.rank < lowest.rank) lowest = { key: one.key, rank: one.rank };
  }
  return lowest?.key ?? null;
};

/**
 * Every capability of one scope, in the catalogue's own order, answered against
 * one level.
 *
 * `ord` and not the array order: the server orders by it already, but a screen
 * that re-sorts a filtered slice by hand is a screen that drifts.
 */
export const capabilityChoices = (
  catalogue: CapabilityCatalogueResponse,
  authority: Authority,
  scope: 'record' | 'tenant',
): CapabilityChoice[] => {
  const held = rankOf(catalogue, authority);
  return catalogue.capabilities
    .filter((row) => row.scope === scope)
    .slice()
    .sort((left, right) => left.ord - right.ord)
    .map((row) => ({
      row,
      allowed: held >= row.minRank,
      needsAuthority: held >= row.minRank ? null : lowestAuthorityFor(catalogue, row.minRank),
    }));
};

/* ---------------------------------------------------------------- an impact */

/**
 * What the preview means, in the units a sentence uses.
 *
 * The server counts grants; a person reads people. `skippedCustomised` is one
 * entry per (person, record), so counting its length answers "3 will not move"
 * when the truth is one person in three records. `heldBackPeople` is the
 * distinct count, and `heldBackGrants` is the raw one, because the sentence
 * "one person, three records" needs both.
 */
export type ImpactReading = {
  readonly people: number;
  readonly records: number;
  readonly grantsToWrite: number;
  /** Distinct people whose grants were set by hand and will not move. */
  readonly heldBackPeople: number;
  /** Rows that will not move: one per person per record. */
  readonly heldBackGrants: number;
  readonly notMembers: readonly string[];
  readonly belowLevel: readonly string[];
  /** True when the write would change nothing at all. */
  readonly reachesNobody: boolean;
};

export const readImpact = (answer: JobImpactResponse): ImpactReading => {
  const heldBack = new Set(answer.skippedCustomised.map((one) => one.userId));
  const notMembers: string[] = [];
  const belowLevel: string[] = [];
  for (const one of answer.refused) {
    if (one.reason === 'not_a_member') notMembers.push(one.userId);
    else belowLevel.push(one.userId);
  }
  return {
    people: answer.people,
    records: answer.records,
    grantsToWrite: answer.grantsToWrite,
    heldBackPeople: heldBack.size,
    heldBackGrants: answer.skippedCustomised.length,
    notMembers,
    belowLevel,
    reachesNobody: answer.grantsToWrite === 0,
  };
};
